import { useCreateNovel } from '@/hooks/useCreateNovel';
import { IntelligenceTier, IntelligenceTierEnum, NovelLanguage, NovelLanguageEnum } from '@common/schemas/novel.schema';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@/hooks/useSubscription';

interface CreateNovelDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CreateNovelDialog({ open, onClose }: CreateNovelDialogProps) {
  const { t, i18n } = useTranslation();
  const { createNovel } = useCreateNovel();
  const { hasFeature } = useSubscription();
  const isGenerativeAiAllowed = hasFeature('is_generative_ai_allowed');
  const [title, setTitle] = useState('');
  const [language, setLanguage] = useState<NovelLanguage>('English');
  const [themeSelect, setThemeSelect] = useState('fantasy');
  const [customTheme, setCustomTheme] = useState('');
  const [audience, setAudience] = useState('');
  const [worldSetting, setWorldSetting] = useState('');
  const [tier, setTier] = useState<IntelligenceTier>('eco');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTitle('');
      const languageByLocale: Record<string, NovelLanguage> = {
        ca: 'Catalan',
        de: 'German',
        en: 'English',
        es: 'Spanish',
        fr: 'French',
        it: 'Italian',
      };
      const userLanguage = (i18n.resolvedLanguage ?? i18n.language).split('-')[0];
      setLanguage(languageByLocale[userLanguage] ?? 'English');
      setThemeSelect('fantasy');
      setCustomTheme('');
      setAudience(t('novel.audiences.adult'));
      setWorldSetting(t('novel.world_settings.fantasy'));
      setTier('eco');
    }
  }, [i18n.language, i18n.resolvedLanguage, open, t]);

  const handleCreate = async () => {
    if (!title.trim()) return;
    const finalTheme = themeSelect === 'custom' ? customTheme.trim() : themeSelect;
    const id = await createNovel({
      title,
      language,
      theme: finalTheme,
      audience,
      worldSetting,
      aiSettings: { intelligenceTier: tier }
    });
    if (id) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        transition: {
          onEntered: () => inputRef.current?.focus(),
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>{t('dashboard.selection_grid.dialog_title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            label={t('dashboard.selection_grid.dialog_label')}
            placeholder={t('dashboard.selection_grid.dialog_placeholder')}
            fullWidth
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            slotProps={{
              input: { sx: { fontSize: '1.25rem', fontWeight: 500 } }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && title.trim()) handleCreate();
            }}
            inputRef={inputRef}
          />

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
              {t('novel.additional_details_opt')}
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('dashboard.settings.generation_language')}</InputLabel>
                  <Select
                    value={language}
                    label={t('dashboard.settings.generation_language')}
                    onChange={(e) => setLanguage(e.target.value as NovelLanguage)}
                  >
                    {NovelLanguageEnum.options.map(option => (
                      <MenuItem key={option} value={option}>
                        {t(`language.${option.toLowerCase()}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('novel.theme')}</InputLabel>
                  <Select
                    value={themeSelect}
                    label={t('novel.theme')}
                    onChange={(e) => {
                      setThemeSelect(e.target.value);
                      if (e.target.value !== 'custom') {
                        setCustomTheme('');
                      }
                    }}
                  >
                    <MenuItem value="fantasy">{t('novel.themes.fantasy')}</MenuItem>
                    <MenuItem value="scifi">{t('novel.themes.scifi')}</MenuItem>
                    <MenuItem value="police">{t('novel.themes.police')}</MenuItem>
                    <MenuItem value="horror">{t('novel.themes.horror')}</MenuItem>
                    <MenuItem value="drama">{t('novel.themes.drama')}</MenuItem>
                    <MenuItem value="romance">{t('novel.themes.romance')}</MenuItem>
                    <MenuItem value="dark">{t('novel.themes.dark')}</MenuItem>
                    <MenuItem value="custom">{t('novel.themes.custom')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {themeSelect === 'custom' && (
                <Grid size={{ xs: 6 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label={t('novel.themes.custom_placeholder')}
                    value={customTheme}
                    onChange={(e) => setCustomTheme(e.target.value)}
                  />
                </Grid>
              )}

              <Grid size={{ xs: 6 }}>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={[
                    t('novel.audiences.adult'),
                    t('novel.audiences.young_adult'),
                    t('novel.audiences.children')
                  ]}
                  value={audience}
                  onInputChange={(_, newValue) => setAudience(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label={t('novel.audience')} fullWidth />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 6 }}>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={[
                    t('novel.world_settings.fantasy'),
                    t('novel.world_settings.ancient_world'),
                    t('novel.world_settings.victorian_era'),
                    t('novel.world_settings.sci_fi')
                  ]}
                  value={worldSetting}
                  onInputChange={(_, newValue) => setWorldSetting(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label={t('novel.world_setting')} fullWidth />
                  )}
                />
              </Grid>

              {isGenerativeAiAllowed && (
                <Grid size={{ xs: 6 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('dashboard.settings.intelligence_tier')}</InputLabel>
                    <Select
                      value={tier}
                      label={t('dashboard.settings.intelligence_tier')}
                      onChange={(e) => setTier(e.target.value as IntelligenceTier)}
                    >
                      {IntelligenceTierEnum.options.map(opt => (
                        <MenuItem key={opt} value={opt}>
                          {t(`dashboard.settings.intelligence_tiers.${opt}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose}>{t('dashboard.selection_grid.cancel')}</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!title.trim()}
        >
          {t('dashboard.selection_grid.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
